/**
 * The capability list — one copy, from which the type is derived.
 *
 * 05 section 7 asks for a single source of truth. The database has
 * `platform_capabilities`; this is its counterpart on the TypeScript side, and
 * the `Capability` union is *derived* from it rather than written out again, so
 * the list and the type cannot disagree.
 *
 * The remaining risk is that this file and the table drift. That is not left to
 * discipline: `tests-integration/capabilities.test.ts` queries the live table
 * and fails if the two sets differ in either direction. Adding a capability is
 * a migration plus an entry here, and the test is what says you did both.
 *
 * The reference implementation had this list three times — a TypeScript
 * constant, a SQL function, and a third copy in the digest builder — and they
 * drifted in production: a Manager restricted to their own accounts on screen
 * received the whole company's book by email every morning. Nobody sees
 * somebody else's inbox, so it went unnoticed.
 */

export interface CapabilityDefinition {
    code: string;
    /** Groups the checkbox list on the role editor. Matches the table's `area`. */
    area: 'customers' | 'workflow' | 'cheques' | 'reports' | 'stock' | 'data' | 'team' | 'settings' | 'danger';
    label: string;
    description: string;
}

export const CAPABILITIES = [
    { code: 'customers.view.assigned', area: 'customers', label: 'See assigned accounts', description: 'The accounts this person owns or collects on.' },
    { code: 'customers.view.all', area: 'customers', label: 'See the whole book', description: 'Every account in the company, not only their own.' },
    { code: 'customers.create', area: 'customers', label: 'Add an account', description: '' },
    { code: 'customers.edit', area: 'customers', label: 'Edit account details', description: 'Contact details, category, rank.' },
    { code: 'customers.edit.financials', area: 'customers', label: 'Edit the figures', description: 'Balances and ageing. Enforced by the database, not only hidden.' },
    { code: 'customers.delete', area: 'customers', label: 'Delete an account', description: '' },
    { code: 'customers.assign', area: 'customers', label: 'Assign owner or collector', description: 'Who chases whom. Enforced by the database.' },

    { code: 'followups.record', area: 'workflow', label: 'Record follow-ups', description: 'Promises, next dates, forecasts.' },
    { code: 'activity.write', area: 'workflow', label: 'Add to the history', description: '' },
    { code: 'activity.delete.own', area: 'workflow', label: 'Remove own entries', description: '' },

    { code: 'cheques.view', area: 'cheques', label: 'See cheques', description: '' },
    { code: 'cheques.manage', area: 'cheques', label: 'Record and clear cheques', description: 'Record, clear, hold, bounce.' },

    { code: 'reports.view.own', area: 'reports', label: 'See own reports', description: '' },
    { code: 'reports.view.all', area: 'reports', label: 'See company reports', description: '' },
    { code: 'reports.export', area: 'reports', label: 'Export reports', description: 'Take data out of the product.' },
    { code: 'ai.generate', area: 'reports', label: 'Generate AI reports', description: 'Send selected facts about an account they can already see to the company’s AI provider, using the company’s AI quota.' },

    { code: 'stock.view', area: 'stock', label: 'See stock', description: '' },
    { code: 'stock.view.prices', area: 'stock', label: 'See stock prices', description: 'Rate and value columns. The server blanks them for everyone else.' },

    { code: 'datasource.view', area: 'data', label: 'See the data source', description: '' },
    { code: 'datasource.sync', area: 'data', label: 'Run a sync', description: '' },
    { code: 'datasource.manage', area: 'data', label: 'Change the data source', description: 'The sheet, the mapping, the identity rule.' },

    { code: 'team.view', area: 'team', label: 'See the team', description: '' },
    { code: 'team.manage', area: 'team', label: 'Manage the team', description: 'Invite, change roles, remove. Cannot grant more than they hold.' },
    { code: 'roles.manage', area: 'team', label: 'Manage roles', description: 'Create and edit roles. Cannot grant more than they hold.' },

    { code: 'templates.manage', area: 'settings', label: 'Manage message templates', description: '' },
    { code: 'settings.manage', area: 'settings', label: 'Manage company settings', description: 'Profile, locale, currency, categories.' },
    { code: 'integrations.manage', area: 'settings', label: 'Manage integrations', description: 'Stock sheet, AI key, email schedule.' },

    { code: 'workspace.reset', area: 'danger', label: 'Fresh start', description: 'Clear the company’s accounts, history, promises, cheques and imports. People, roles and settings stay.' },
    { code: 'workspace.audit.view', area: 'danger', label: 'See the audit log', description: 'Who did what, and when.' },
    { code: 'workspace.export', area: 'danger', label: 'Export company data', description: 'Download every account, history entry, cheque and setting. Never includes keys or credentials.' },
    { code: 'workspace.delete', area: 'danger', label: 'Delete the company', description: 'The owner alone, and never grantable to a role. Deletion waits seven days and can be cancelled.' },
] as const satisfies readonly CapabilityDefinition[];

/**
 * What someone is allowed to do inside one company.
 *
 * Derived from the list above rather than written out beside it, so a
 * capability cannot exist in one and not the other.
 */
export type Capability = (typeof CAPABILITIES)[number]['code'];

export type CapabilityArea = CapabilityDefinition['area'];

/** The order the role editor shows them in, and what each group is called. */
export const CAPABILITY_AREAS: readonly { area: CapabilityArea; title: string }[] = [
    { area: 'customers', title: 'Accounts' },
    { area: 'workflow', title: 'Follow-ups and history' },
    { area: 'cheques', title: 'Cheques' },
    { area: 'reports', title: 'Reports' },
    { area: 'stock', title: 'Stock' },
    { area: 'data', title: 'Data source' },
    { area: 'team', title: 'People and roles' },
    { area: 'settings', title: 'Company settings' },
    { area: 'danger', title: 'The company itself' },
];

const BY_CODE = new Map<string, CapabilityDefinition>(
    CAPABILITIES.map(c => [c.code, c as CapabilityDefinition]),
);

export const capabilityDefinition = (code: string): CapabilityDefinition | undefined =>
    BY_CODE.get(code);

/** A code this build has never heard of. Shown as itself rather than hidden. */
export const isKnownCapability = (code: string): code is Capability => BY_CODE.has(code);

export const capabilitiesIn = (area: CapabilityArea): readonly CapabilityDefinition[] =>
    CAPABILITIES.filter(c => c.area === area) as readonly CapabilityDefinition[];

/**
 * Owner-only, and never grantable to a role.
 *
 * The role editor does not offer it; `guard_role_capabilities` refuses it on a
 * non-owner role; `decideCapability()` refuses it to a non-owner even if a row
 * somehow carries it. Three places, because it is the one that ends a company.
 */
export const OWNER_ONLY: readonly Capability[] = ['workspace.delete'];

export const isGrantable = (code: string): boolean =>
    isKnownCapability(code) && !OWNER_ONLY.includes(code);
